import { zodResolver } from '@hookform/resolvers/zod';
import capitalize from 'lodash/capitalize';
import React from 'react';
import { useForm } from 'react-hook-form';

import {
  CreatePromptTemplate,
  CreatePromptTemplateSchema,
} from '@eluve/api-contract';
import { Tooltip, TooltipContent, TooltipTrigger } from '@eluve/components';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Form,
  FormField,
  FormItem,
  FormMessage,
  Heading,
  Icon,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Separator,
  Stack,
  Text,
  Textarea,
} from '@eluve/components';
import { LlmOutputTypesLookup } from '@eluve/graphql-types';
import {
  LLM_OUTPUT_TYPE_DESCRIPTIONS,
  LLM_OUTPUT_TYPE_VARIABLES,
} from '@eluve/llm-outputs';
import { useNamedLogger } from '@eluve/logger';

const formDefaults: Partial<CreatePromptTemplate> = {
  name: '',
  outputType: LlmOutputTypesLookup.DYNAMIC_OUTPUT,
  template: [
    { role: 'system', content: '' },
    { role: 'user', content: '' },
  ],
};

export interface CreatePromptTemplateFormProps {
  initialFormValues?: Partial<CreatePromptTemplate>;
  onCancel: () => void;
  onSubmit: (data: CreatePromptTemplate) => void;
  shouldShowMetadata?: boolean;
}

export const CreatePromptTemplateForm: React.FC<
  CreatePromptTemplateFormProps
> = ({ initialFormValues, onCancel, onSubmit, shouldShowMetadata = true }) => {
  const logger = useNamedLogger('CreatePromptTemplateForm');
  const defaultValues = initialFormValues ? initialFormValues : formDefaults;

  const form = useForm<CreatePromptTemplate>({
    resolver: zodResolver(CreatePromptTemplateSchema),
    defaultValues,
    mode: 'onChange',
  });

  const selectedOutputType = form.watch('outputType');

  const availableTemplateVariables =
    LLM_OUTPUT_TYPE_VARIABLES[selectedOutputType];
  const llmOutputTypeDescription =
    LLM_OUTPUT_TYPE_DESCRIPTIONS[selectedOutputType];

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit, (invalid) =>
          logger.debug(
            `Invalid form submission attempted: ${JSON.stringify(invalid)}`,
          ),
        )}
      >
        <Stack justify="end" className="-mt-14" gap={2} direction="horizontal">
          <Button disabled={!form.formState.isDirty} type="submit">
            Create
          </Button>
          <Button variant="secondary" onClick={onCancel}>
            Cancel
          </Button>
        </Stack>
        <Separator className="mt-6 mb-4" />
        {shouldShowMetadata && (
          <>
            <Heading size="3xl">Metadata</Heading>
            <FormField
              control={form.control}
              name="outputType"
              render={({ field, fieldState: { error } }) => (
                <FormItem className="mt-4 max-w-lg">
                  <Select
                    value={field.value}
                    onValueChange={(val) => {
                      field.onChange(val);
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Output Type" />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.keys(LLM_OUTPUT_TYPE_VARIABLES)
                        .sort()
                        .filter(
                          (type) => type !== LlmOutputTypesLookup.SOAP_NOTE,
                        )
                        .map((type) => (
                          <SelectItem key={type} value={type}>
                            {type.split('_').map(capitalize).join(' ')}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                  {error && (
                    <FormMessage className="mt-4">{error.message}</FormMessage>
                  )}
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="name"
              render={({ field, fieldState: { error } }) => (
                <FormItem className="max-w-lg">
                  <label htmlFor="name" className="sr-only">
                    Name
                  </label>
                  <Input className="bg-white/5" placeholder="Name" {...field} />
                  {error && (
                    <FormMessage className="mt-4">{error.message}</FormMessage>
                  )}
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="description"
              render={({ field, fieldState: { error } }) => (
                <FormItem className="max-w-lg">
                  <label htmlFor="description" className="sr-only">
                    Description
                  </label>
                  <Textarea
                    className="bg-white/5"
                    placeholder="Description"
                    {...field}
                  />
                  {error && (
                    <FormMessage className="mt-4">{error.message}</FormMessage>
                  )}
                </FormItem>
              )}
            />
            <Separator className="mt-8 mb-4" />
          </>
        )}

        <Heading size="3xl">Prompt Variant</Heading>

        {Boolean(availableTemplateVariables?.length) && (
          <Alert className="mt-4">
            <Icon name="Rocket" />
            <AlertTitle>Heads up!</AlertTitle>
            <AlertDescription>
              <Text>
                You can substitute the following variables in your messages:{' '}
                {availableTemplateVariables.map((variable) => (
                  <Tooltip key={variable}>
                    <TooltipTrigger asChild>
                      <span>
                        <code className="rounded-sm bg-border-tertiary p-0.5 text-text-primary">{`{{${variable}}}`}</code>{' '}
                      </span>
                    </TooltipTrigger>
                    <TooltipContent
                      align="start"
                      alignOffset={-50}
                      className="max-w-60 border-none bg-white text-wrap text-text-secondary shadow-md"
                    >
                      {llmOutputTypeDescription?.[variable]}
                    </TooltipContent>
                  </Tooltip>
                ))}
              </Text>
            </AlertDescription>
          </Alert>
        )}
        <FormField
          control={form.control}
          name="template"
          render={({ field, fieldState: { error } }) => (
            <>
              {error && <FormMessage>{error.message}</FormMessage>}
              <FormItem className="w-full">
                <Label>System Message</Label>
                <Textarea
                  rows={10}
                  className="bg-white/5"
                  placeholder="Include system context to set the stage for the prompt"
                  value={field.value[0]?.content}
                  onChange={(e) => {
                    const fieldValue = field.value;
                    const systemMessage = fieldValue[0];
                    if (systemMessage) {
                      systemMessage.content = e.target.value;
                      field.onChange(fieldValue);
                    }
                  }}
                />
              </FormItem>
              <FormItem className="w-full">
                <Label>User Message</Label>
                <Textarea
                  rows={10}
                  className="bg-white/5"
                  placeholder="Instructions and context for the LLM about what it should do"
                  value={field.value[1]?.content}
                  onChange={(e) => {
                    const fieldValue = field.value;
                    const userMessage = fieldValue[1];
                    if (userMessage) {
                      userMessage.content = e.target.value;
                      field.onChange(fieldValue);
                    }
                  }}
                />
              </FormItem>
            </>
          )}
        />

        <Stack className="mt-2" gap={2}>
          <FormField
            control={form.control}
            name="variantNotes"
            render={({ field, fieldState: { error } }) => (
              <FormItem className="w-full">
                <Label>Notes (Internal)</Label>
                <Input
                  className="bg-white/5"
                  placeholder="Jot down any internal notes about this template. These are not visible to the user."
                  value={field.value ?? ''}
                  onChange={(e) => field.onChange(e.target.value)}
                />
                {error && (
                  <FormMessage className="mt-4">{error.message}</FormMessage>
                )}
              </FormItem>
            )}
          />
        </Stack>
      </form>
    </Form>
  );
};
